// components/PlaidLink/PlaidLink.js
import React, { useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useDispatch, useSelector } from 'react-redux';
import {
    createLinkToken,
    exchangePublicToken,
    clearLinkToken,
    clearError,
} from '../../redux/slices/plaidSlice';
import classes from './PlaidLink.module.css';

const PlaidLink = ({
    onSuccess,
    onExit,
    children,
    className,
    disabled = false
}) => {
    const dispatch = useDispatch();
    const {
        linkToken,
        linkTokenLoading,
        linkTokenError,
        loading,
        error,
    } = useSelector((state) => state.plaid);

    // Create link token when component mounts
    useEffect(() => {
        if (!linkToken && !linkTokenLoading && !linkTokenError) {
            dispatch(createLinkToken());
        }
    }, [dispatch, linkToken, linkTokenLoading, linkTokenError]);

    // Handle successful Plaid Link flow
    const handleOnSuccess = useCallback(async (public_token, metadata) => {
        try {
            const result = await dispatch(exchangePublicToken({
                publicToken: public_token,
                metadata: metadata,
            })).unwrap();

            // Call parent's onSuccess callback
            if (onSuccess) {
                onSuccess(result);
            }

            // Clear the link token since connection is complete
            dispatch(clearLinkToken());
        } catch (error) {
            console.error('Failed to exchange public token:', error);
        }
    }, [dispatch, onSuccess]);

    // Handle Plaid Link exit (user cancels or closes)
    const handleOnExit = useCallback((err, metadata) => {
        if (err != null) {
            console.error('Plaid Link exit error:', err);
        }

        // Clear any errors and call parent's onExit callback
        dispatch(clearError());
        if (onExit) {
            onExit(err, metadata);
        }
    }, [dispatch, onExit]);

    // Initialize Plaid Link
    const { open, ready, error: plaidLinkError } = usePlaidLink({
        token: linkToken,
        onSuccess: handleOnSuccess,
        onExit: handleOnExit,
        onEvent: (eventName, metadata) => {
            // Log events for debugging (optional)
            console.log('Plaid Link event:', eventName, metadata);
        },
    });

    // Handle click to open Plaid Link
    const handleClick = useCallback(() => {
        if (ready && !disabled && !loading) {
            open();
        } else if (!linkToken && !linkTokenLoading) {
            // Retry creating link token if it failed
            dispatch(createLinkToken());
        }
    }, [ready, disabled, loading, open, linkToken, linkTokenLoading, dispatch]);

    // Determine button state and text
    const getButtonState = () => {
        if (linkTokenLoading) {
            return { text: 'Initializing...', isDisabled: true };
        }
        if (loading) {
            return { text: 'Connecting...', isDisabled: true };
        }
        if (linkTokenError) {
            return { text: 'Retry Connection', isDisabled: false };
        }
        if (!ready) {
            return { text: 'Preparing...', isDisabled: true };
        }
        if (disabled) {
            return { text: children || 'Connect Bank', isDisabled: true };
        }
        return { text: children || 'Connect Bank', isDisabled: false };
    };

    const { text, isDisabled } = getButtonState();

    // Show error message if there's an error
    const hasError = linkTokenError || error || plaidLinkError;

    return (
        <div className={classes.plaidLinkContainer}>
            <button
                type="button"
                className={`${classes.plaidLinkButton} ${className || ''} ${isDisabled ? classes.disabled : ''
                    }`}
                onClick={handleClick}
                disabled={isDisabled}
            >
                {loading && <span className={classes.spinner}></span>}
                {text}
            </button>

            {hasError && (
                <div className={classes.errorMessage}>
                    {linkTokenError || error || plaidLinkError?.message || 'Connection failed'}
                    <button
                        type="button"
                        className={classes.retryButton}
                        onClick={() => {
                            dispatch(clearError());
                            dispatch(createLinkToken());
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}
        </div>
    );
};

export default PlaidLink;